import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, Calendar, ChevronRight, Zap, Navigation } from 'lucide-react';
import { eventsAPI } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { detectGeo } from '../lib/geo';
import { CATEGORIES as EVENT_CATEGORIES, CATEGORY_LABELS as BASE_CATEGORY_LABELS, CATEGORY_COLORS } from '../lib/eventMeta';

const CATEGORIES = ['all', ...EVENT_CATEGORIES.map(c => c.value)];
const CATEGORY_LABELS = { all: 'All', ...BASE_CATEGORY_LABELS };

const EventCard = ({ event }) => {
  const { format } = useCurrency();
  const minPrice = event.ticket_types?.length
    ? Math.min(...event.ticket_types.map(t => t.price))
    : 0;

  return (
    <Link to={`/events/${event.event_id}`} data-testid={`event-card-${event.event_id}`}>
      <div className="event-card bg-[#0A0A0A] border border-gray-800 rounded-lg overflow-hidden h-full flex flex-col">
        <div className="relative aspect-[16/9] overflow-hidden">
          <img
            src={event.image_url || 'https://images.unsplash.com/photo-1765278797923-10a027f5c69d?w=600&q=80'}
            alt={event.title}
            className="w-full h-full object-cover"
            style={{ transition: 'transform 0.4s ease' }}
            onMouseEnter={e => e.target.style.transform = 'scale(1.05)'}
            onMouseLeave={e => e.target.style.transform = 'scale(1)'}
          />
          {event.is_featured && (
            <span className="absolute top-3 left-3 gold-badge px-2 py-0.5 rounded-sm text-[10px] font-black uppercase">
              Featured
            </span>
          )}
          <span className={`absolute top-3 right-3 px-2 py-0.5 rounded text-[10px] font-bold uppercase ${CATEGORY_COLORS[event.category] || 'bg-gray-500/20 text-gray-400'}`}>
            {CATEGORY_LABELS[event.category] || event.category}
          </span>
        </div>
        <div className="p-4 flex flex-col flex-1">
          <h3 className="font-black uppercase text-sm md:text-base text-white leading-tight mb-2 line-clamp-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {event.title}
          </h3>
          <div className="space-y-1.5 mb-3 flex-1">
            <div className="flex items-center gap-1.5 text-gray-400 text-xs">
              <Calendar size={11} className="flex-shrink-0" />
              <span>{event.date} {event.time && `• ${event.time}`}</span>
            </div>
            <div className="flex items-center gap-1.5 text-gray-400 text-xs">
              <MapPin size={11} className="flex-shrink-0" />
              <span className="truncate">{event.city}, {event.country}</span>
            </div>
          </div>
          <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-800">
            <div>
              {minPrice === 0 ? (
                <span className="text-green-400 font-bold text-sm uppercase">Free</span>
              ) : (
                <span className="text-white font-bold text-sm">From {format(minPrice)}</span>
              )}
            </div>
            <span className="btn-primary text-xs px-3 py-1.5 rounded flex items-center gap-1">
              Buy <ChevronRight size={12} />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

const SkeletonCard = () => (
  <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg overflow-hidden">
    <div className="skeleton aspect-[16/9]" />
    <div className="p-4 space-y-3">
      <div className="skeleton h-4 w-3/4 rounded" />
      <div className="skeleton h-3 w-1/2 rounded" />
      <div className="skeleton h-3 w-2/3 rounded" />
      <div className="skeleton h-8 w-full rounded mt-2" />
    </div>
  </div>
);

export default function ExplorePage() {
  const { t } = useLanguage();
  const [events, setEvents] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [nearYou, setNearYou] = useState([]);
  const [detectedCity, setDetectedCity] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [city, setCity] = useState('');
  const [when, setWhen] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [heroIdx, setHeroIdx] = useState(0);

  const HERO_IMAGES = [
    'https://images.unsplash.com/photo-1765278797923-10a027f5c69d?w=1600&q=85',
    'https://images.unsplash.com/photo-1571575522341-be760794a35b?w=1600&q=85',
  ];

  useEffect(() => {
    const timer = setInterval(() => setHeroIdx(i => (i + 1) % HERO_IMAGES.length), 5000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    eventsAPI.list({ featured: true, limit: 5 }).then(r => setFeatured(r.data.data || [])).catch(() => {});
    // Geo-based "Near You"
    detectGeo().then(geo => {
      if (geo?.city) {
        setDetectedCity(geo.city);
        eventsAPI.list({ city: geo.city, limit: 4 }).then(r => {
          if ((r.data.data || []).length > 0) setNearYou(r.data.data);
        }).catch(() => {});
      }
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { page, limit: 12 };
    if (category !== 'all') params.category = category;
    if (search) params.search = search;
    if (city) params.city = city;
    if (when) params.when = when;
    eventsAPI.list(params).then(r => {
      setEvents(r.data.data || []);
      setTotal(r.data.total || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [category, search, city, when, page]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="explore-page">
      {/* Hero */}
      <section className="relative h-[55vh] md:h-[70vh] overflow-hidden">
        <div className="absolute inset-0">
          {HERO_IMAGES.map((img, i) => (
            <div
              key={i}
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${img})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                opacity: i === heroIdx ? 1 : 0,
                transition: 'opacity 1s ease-in-out'
              }}
            />
          ))}
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative h-full flex flex-col items-center justify-center text-center px-4 pt-16">
          <div className="stagger-1">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-yellow-400 mb-4 px-3 py-1 border border-yellow-400/30 rounded-full">
              <Zap size={10} fill="currentColor" /> #adventure
            </span>
          </div>
          <h1 className="stagger-2 text-4xl sm:text-5xl lg:text-7xl font-black uppercase tracking-tighter text-white leading-[0.9] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {t('heroTitle')}
          </h1>
          <p className="stagger-3 text-gray-300 text-base md:text-lg max-w-xl mb-8">
            {t('heroSubtitle')}
          </p>
          {/* Search bar */}
          <div className="stagger-4 w-full max-w-xl">
            <div className="relative">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder={t('search')}
                className="w-full bg-black/60 border border-gray-700 text-white pl-11 pr-4 py-3.5 rounded-lg text-sm focus:border-red-500 backdrop-blur-sm"
                data-testid="search-input"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        {/* Featured events strip */}
        {featured.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-black uppercase tracking-tight text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {t('featuredEvents')}
              </h2>
              <span className="gold-badge px-3 py-1 rounded-sm text-xs">#ADVENTURE</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {featured.slice(0, 3).map(event => (
                <EventCard key={event.event_id} event={event} />
              ))}
            </div>
          </section>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          {/* Category pills */}
          <div className="flex items-center gap-2 flex-wrap">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => { setCategory(cat); setPage(1); }}
                className={`category-pill px-4 py-1.5 rounded-full text-xs font-bold uppercase border transition-colors ${
                  category === cat
                    ? 'bg-red-600 border-red-600 text-white'
                    : 'border-gray-700 text-gray-400 hover:border-gray-500 hover:text-white'
                }`}
                data-testid={`category-${cat}`}
              >
                {CATEGORY_LABELS[cat]}
              </button>
            ))}
          </div>
          {/* Time quick filters */}
          <div className="flex items-center gap-2">
            {[{ key: 'today', label: 'Today' }, { key: 'weekend', label: 'This Weekend' }].map(opt => (
              <button
                key={opt.key}
                onClick={() => { setWhen(w => w === opt.key ? '' : opt.key); setPage(1); }}
                className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase border transition-colors ${
                  when === opt.key
                    ? 'bg-red-600 border-red-600 text-white'
                    : 'border-gray-700 text-gray-400 hover:border-gray-500 hover:text-white'
                }`}
                data-testid={`when-${opt.key}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {/* City filter */}
          <div className="flex items-center gap-2 md:ml-auto">
            <MapPin size={14} className="text-gray-500" />
            <input
              type="text"
              value={city}
              onChange={e => { setCity(e.target.value); setPage(1); }}
              placeholder={t('filterByCity')}
              className="bg-transparent border border-gray-700 text-white text-sm px-3 py-1.5 rounded focus:border-red-500 w-40"
              data-testid="city-filter"
            />
            {detectedCity && (
              <button
                onClick={() => { setCity(detectedCity); setPage(1); }}
                className="flex items-center gap-1 text-xs text-red-400 border border-red-600/30 hover:bg-red-600/10 px-2.5 py-1.5 rounded transition-colors"
                data-testid="near-me-btn"
                title={`Events in ${detectedCity}`}
              >
                <Navigation size={11} /> {t('nearMeBtn')}
              </button>
            )}
          </div>
        </div>

        {/* Near You section (shown when geo is detected) */}
        {nearYou.length > 0 && (
          <section className="mb-12" data-testid="near-you-section">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Navigation size={16} className="text-red-400" />
                <h2 className="text-2xl font-black uppercase tracking-tight text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {t('nearYou')}
                </h2>
                <span className="text-gray-500 text-xs font-medium border border-gray-700 px-2 py-0.5 rounded-full">
                  {detectedCity}
                </span>
              </div>
              <button
                onClick={() => { setCity(detectedCity); setPage(1); }}
                className="text-red-400 text-xs font-bold hover:text-red-300 transition-colors"
              >
                {t('viewAll')} →
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {nearYou.map(event => <EventCard key={event.event_id} event={event} />)}
            </div>
          </section>
        )}

        {/* Events grid */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-black uppercase tracking-tight text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {t('upcomingEvents')}
            </h2>
            <span className="text-gray-500 text-sm">{total} events</span>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg font-medium">{t('noResults')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {events.map(event => <EventCard key={event.event_id} event={event} />)}
            </div>
          )}

          {/* Pagination */}
          {total > 12 && (
            <div className="flex items-center justify-center gap-2 mt-10">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 border border-gray-700 rounded text-sm text-gray-400 hover:border-gray-500 disabled:opacity-40"
              >
                {t('back')}
              </button>
              <span className="text-gray-400 text-sm">Page {page} of {Math.ceil(total / 12)}</span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= Math.ceil(total / 12)}
                className="px-4 py-2 border border-gray-700 rounded text-sm text-gray-400 hover:border-gray-500 disabled:opacity-40"
              >
                {t('next')}
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
